import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import de from 'dayjs/locale/de';
import relativeTime from 'dayjs/plugin/relativeTime';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeekInYear from 'dayjs/plugin/isoWeeksInYear';

/**
 * Days supported Time Formats
 * @see https://day.js.org/docs/en/display/format
 */
const TimeFormats = {
  DAY_MONTH_YEAR: 'DD.MM.YYYY',
  DAY_MONTH_YEAR_SHORT: 'DD.MM.YY',
  DAY_MONTH_YEAR_HOURS_MINUTES: 'DD.MM.YYYY, HH:mm',
  DAY_MONTH_YEAR_HOURS_MINUTES_SECONDS: 'DD.MM.YYYY, HH:mm:ss',
  HOUR_MINUTE: 'HH:mm',
  DAY_MONTH_YEARH_OPEN_BRACET_HOUR_MINUTE: 'DD.MM.YYYY (HH:mm',
  SHORTNAME_WEEK_DAY_MONTH_YEAR: 'dddd, DD.MM.YYYY',
  MONTH_NAME: 'MMMM',
  MONTH_YEAR: 'MMMM YYYY',
  DAY_MONTH_YEAR_READABLE: 'D. MMMM YYYY',
  SHORT_WEEKDAY_DAY_MONTH_YEAR: 'dd. D. MMMM YYYY',
  ISO_DATE: 'YYYY-MM-DD',
  YEAR: 'YYYY',
};

const supportedLocales = {
  de,
};

dayjs.locale(supportedLocales.de);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(isoWeek);
dayjs.extend(isoWeekInYear);

/**
 * Reformats a datetime string into a desired format.
 * */
const formatDate = (string: string, dateFormat: string) => {
  if (!string) {
    return null;
  }
  return dayjs(string).format(dateFormat);
};

const formatTime = (string: string) => {
  if (!string) {
    return null;
  }

  // DayJS does not do this natively, so we need to set the format for a value like "10:45"
  const date = dayjs(string, 'HH:MM').format('HH:MM');
  return `${date}`;
};

const formatWeek = (string: string) => {
  if (!string) {
    return null;
  }

  const [year, week] = string.split('-W');
  const date = dayjs(`${year}-01-01`).isoWeek(Number(week));
  return `${date.isoWeek()}, ${date.isoWeekYear()}`;
};

/**
 * Check wether the first date lies between the 2nd and 3rd, including the given dates.
 * */
const isBetween = (string: string, start: string, end: string) => {
  const date = dayjs(string);
  return date >= dayjs(start) && date <= dayjs(end);
};

const isDateAfter = (date: string, dateToCampareTo: string) => {
  return dayjs(date).isAfter(dayjs(dateToCampareTo));
};

const isDateBefore = (date: string, dateToCampareTo: string) => {
  return dayjs(date).isBefore(dayjs(dateToCampareTo));
};

const isMatch = (dateString: string, compareFormat: dayjs.OptionType) => dayjs(dateString, compareFormat).isValid();

const dateFromNow = (dateString: string) => dayjs(dateString).fromNow();

export { isMatch, formatDate, formatWeek, isBetween, isDateAfter, isDateBefore, dateFromNow, TimeFormats, formatTime };
